
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Command, CommandList } from '@/components/ui/command';
import { Attendee } from '@/types/attendee';
import { motion } from 'framer-motion';
import SearchInput from './SearchInput';
import SearchHistory from './SearchHistory';
import SearchResults from './SearchResults';
import { removeVietnameseAccents } from '@/components/admin/table/tableUtils';

interface SearchDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  attendees: Attendee[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onAttendeeSelect: (attendee: Attendee) => void;
  recentSearches: string[];
}

const SearchDialog: React.FC<SearchDialogProps> = ({
  isOpen,
  onOpenChange,
  attendees,
  searchQuery,
  setSearchQuery,
  onAttendeeSelect,
  recentSearches
}) => {
  const [results, setResults] = useState<Attendee[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  
  useEffect(() => {
    if (!isOpen) {
      setResults([]);
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (!isOpen || !attendees.length) return;
    
    setIsSearching(true);
    
    const timer = setTimeout(() => {
      if (!searchQuery.trim()) {
        setResults([]);
        setIsSearching(false);
        return;
      }
      
      const filtered = attendees
        .filter(attendee => {
          const searchLower = removeVietnameseAccents(searchQuery.toLowerCase());
          return (
            (attendee.name && removeVietnameseAccents(attendee.name.toLowerCase()).includes(searchLower)) ||
            (attendee.email && removeVietnameseAccents(attendee.email.toLowerCase()).includes(searchLower)) ||
            (attendee.phone && removeVietnameseAccents(attendee.phone.toLowerCase()).includes(searchLower)) ||
            (attendee.code && removeVietnameseAccents(attendee.code.toLowerCase()).includes(searchLower)) ||
            (attendee.class && removeVietnameseAccents(attendee.class.toLowerCase()).includes(searchLower))
          );
        })
        .slice(0, 10);
      
      setResults(filtered);
      setIsSearching(false);
    }, 150);
    
    return () => clearTimeout(timer);
  }, [searchQuery, attendees, isOpen]);
  
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };
  
  const handleHistorySelect = (search: string) => {
    setSearchQuery(search);
  };
  
  const handleAttendeeSelect = (attendee: Attendee) => {
    console.log('Selected attendee from search dialog:', attendee);
    onAttendeeSelect(attendee);
    onOpenChange(false);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent 
        className="p-4 sm:max-w-md max-w-[92%] rounded-xl border border-amber-200 dark:border-amber-700/40 bg-gradient-to-b from-white to-amber-50/70 dark:from-gray-800 dark:to-gray-800/95"
        onInteractOutside={(e) => {
          if (isSearching) {
            e.preventDefault();
          }
        }}
      >
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="space-y-4"
        >
          <h3 className="font-medium text-lg mb-3 text-amber-900 dark:text-amber-100">
            Tìm kiếm người tham dự
          </h3>
          
          <Command className="rounded-lg border shadow-md overflow-visible">
            <div className="p-2">
              <SearchInput
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Tìm tên, mã, SĐT, email..."
                autoFocus={true}
              />
            </div>
            
            <CommandList className="max-h-[50vh] overflow-auto">
              {!searchQuery && recentSearches.length > 0 && (
                <SearchHistory 
                  recentSearches={recentSearches} 
                  onSelect={handleHistorySelect}
                />
              )}
              
              <SearchResults 
                results={results}
                searchQuery={searchQuery}
                onSelect={handleAttendeeSelect}
                isLoading={isSearching}
              />
            </CommandList>
          </Command>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
